import { GridApiCommon, GridCellModes, GridRowId, GridRowModes, GridRowModesModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { create } from "zustand";

import { Airports } from "@/constants";
import { PassengerTransferType } from "@/constants/passengerTransferTypes";
import { PASSENGER_STATUS } from "@/constants/status";
import { CommonFilterSwitch } from "@/pages/Plannn/types";
import { CompanionsSchema } from "@/schemas/companions";
import { IPassengerHistoryCompanion } from "@/schemas/passenger-history.schemas";
import { PassengerTypes } from "@/types/global";
import { IPlanPassenger } from "@/types/tasks.types";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type ShowDeletePassengerDialogType = {
    id: string;
    name: string;
    taskAssigned: boolean;
} | null;
type ShowPassengerToWelcomeTeamDialogType = {
    id: string;
    name: string;
    showToWelcomeTeam: CommonFilterSwitch;
} | null;
type ShowUpdateCompanionsDialogType = {
    id: string;
    name: string;
    passengerType: PassengerTypes;
    cancelled: boolean;
    fullCompanions: CompanionsSchema;
} | null;

export enum PlanFilterStatuses {
    ALL,
    ARRIVAL,
    DEPARTURE,
    CANCELLED,
}

type PlanFilters = {
    status: PASSENGER_STATUS | null;
    cancelled: CommonFilterSwitch;
    airport: Airports | null;
    isArrival: CommonFilterSwitch;
    hideSelfTransport: boolean;
    selectedDrivers: string[];
};

export const planKeysToCancel = ["location", "airport", "driverId", "flightNumber", "pickUp", "transferType", "status"];

export const PlanEditableKeys: (keyof PlanEditableFields)[] = [
    "location",
    "airport",
    "driverId",
    "flightNumber",
    "pickUp",
    "transferType",
    "status",
    "companions",
    "note",
    "isSelfTransport",
    "origin",
    "destination",
    "dropOff",
    "note",
    "cancelled",
    "flightTime",
];

export type PlanEditableFields = {
    airport?: string;
    location?: string;
    driverId?: string | null;
    pickUp?: DateTime;
    flightNumber?: string;
    transferType?: PassengerTransferType;
    origin?: string;
    destination?: string;
    companions?: IPassengerHistoryCompanion[];
    dropOff?: DateTime;
    status?: PASSENGER_STATUS;
    note?: string;
    isSelfTransport?: boolean;
    cancelled?: boolean;
    flightTime?: DateTime;
};
type PlanTableState = {
    rowModesModel: GridRowModesModel;
    filters: PlanFilters;
    isFullScreen: boolean;
    changes: Record<GridRowId, PlanEditableFields>;
    initialValues: Record<GridRowId, PlanEditableFields>;
    showConfirmationList: GridRowId[];
    showCreatePassengerDialog: boolean;
    showDeletePassengerDialog: ShowDeletePassengerDialogType;
    showUpdateCompanionsDialog: ShowUpdateCompanionsDialogType;
    showPassengerToWelcomeTeamDialog: ShowPassengerToWelcomeTeamDialogType;
    showPassengerHistoryDialog: string | null;
    searchItemId: string | null;
};

type PlanTableActions = {
    actions: {
        handleRowModesModelChange: (rowModesModel: GridRowModesModel) => void;
        updateFilters: (filters: PlanFilters, apiRef?: GridApiCommon) => void;
        updateFullScreen: (val?: boolean) => void;
        getChangedKeys: (newRow: IPlanPassenger<"api">, oldRow: IPlanPassenger<"api">) => [PlanEditableFields, number];
        processRowUpdate: (newRow: IPlanPassenger<"api">, oldRow: IPlanPassenger<"api">) => IPlanPassenger<"api">;
        handleRowModesModelCancel: (
            id: GridRowId,
            apiRef: GridApiCommon,
            setInitial?: boolean,
            fieldKey?: keyof PlanEditableFields
        ) => void;
        getCellInitialValue: <T>(id: GridRowId, field: keyof PlanEditableFields) => T | false;
        handleShowConfirmationList: (val: GridRowId[]) => void;
        resetRowChanges: (ids?: GridRowId[]) => void;
        resetRowInitials: (ids?: GridRowId[]) => void;
        updatShowCreatePassengerDialog: (val?: boolean) => void;
        updatShowDeletePassengerDialog: (val?: ShowDeletePassengerDialogType) => void;
        updateShowUpdateCompanionsDialog: (val?: ShowUpdateCompanionsDialogType) => void;
        updateShowPassengerToWelcomeTeamDialog: (val?: ShowPassengerToWelcomeTeamDialogType) => void;
        updateShowPassengerHistoryDialog: (val?: string) => void;
        updateSearchItemId: (val: string | null) => void;
    };
};
export const usePlanTableStore = create<PlanTableState & PlanTableActions>()((set, get) => ({
    showConfirmationList: [],
    rowModesModel: {},
    changes: {},
    initialValues: {},
    showCreatePassengerDialog: false,
    showDeletePassengerDialog: null,
    showUpdateCompanionsDialog: null,
    showPassengerToWelcomeTeamDialog: null,
    filters: {
        status: null,
        cancelled: CommonFilterSwitch.FALSE,
        airport: null,
        filterStatus: PlanFilterStatuses.ALL,
        isArrival: CommonFilterSwitch.ALL,
        hideSelfTransport: true,
        selectedDrivers: [],
    },
    isFullScreen: false,
    showPassengerHistoryDialog: null,
    searchItemId: null,
    actions: {
        updateFullScreen: (isFullScreen) => set({ isFullScreen: isFullScreen ?? !get().isFullScreen }),
        updatShowCreatePassengerDialog: (val) => set({ showCreatePassengerDialog: val ?? false }),
        updatShowDeletePassengerDialog: (val) => set({ showDeletePassengerDialog: val ?? null }),
        updateShowUpdateCompanionsDialog: (val) => set({ showUpdateCompanionsDialog: val ?? null }),
        handleShowConfirmationList: (showConfirmationList) => set({ showConfirmationList }),
        updateShowPassengerToWelcomeTeamDialog: (val) => set({ showPassengerToWelcomeTeamDialog: val ?? null }),
        getCellInitialValue: <T>(id: GridRowId, field: keyof PlanEditableFields) =>
            get().initialValues[id] && (get().initialValues[id][field] as T),
        updateShowPassengerHistoryDialog: (val) => set({ showPassengerHistoryDialog: val ?? null }),
        updateSearchItemId: (val) => set({ searchItemId: val }),
        handleRowModesModelChange: (rowModesModel) => set({ rowModesModel }),
        resetRowChanges: (ids?: GridRowId[]) => {
            if (!ids) {
                set({ changes: {} });
                return;
            }
            const mockChanges = { ...get().changes };
            for (const id of ids) {
                delete mockChanges[id];
            }

            set({ changes: mockChanges });
        },
        resetRowInitials: (ids?: GridRowId[]) => {
            if (!ids) {
                set({ initialValues: {} });
                return;
            }
            const initialValues = { ...get().initialValues };
            for (const id of ids) {
                delete initialValues[id];
            }
            set({ initialValues });
        },
        handleRowModesModelCancel: async (id, apiRef, setInitial = true, fieldKey?: string) => {
            try {
                if (!apiRef) return;
                for (const field of planKeysToCancel) {
                    if (fieldKey && fieldKey !== field) continue;
                    const initialData = get().initialValues[id];
                    const initialValue = initialData && get().initialValues[id][field as keyof PlanEditableFields];
                    const cell = {
                        id,
                        field,
                    };
                    const cellMode = apiRef.getCellMode(id, field);

                    if (!setInitial || !initialData || !(field in initialData)) {
                        if (cellMode === GridCellModes.Edit) apiRef.stopCellEditMode(cell);
                        continue;
                    }

                    if (cellMode !== GridCellModes.Edit) {
                        await apiRef.startCellEditMode(cell);
                    }
                    await wait(25);
                    await apiRef.setEditCellValue({
                        id,
                        field,
                        value: initialValue || null,
                    });
                    await wait(25);

                    apiRef.stopCellEditMode(cell);
                }
            } catch (err) {
                console.error("#handleRowModesModelCancel", err);
            }

            await wait(60);
            get().actions.resetRowChanges([id]);
            get().actions.resetRowInitials([id]);
            get().actions.handleRowModesModelChange({
                ...get().rowModesModel,
                [id]: { mode: GridRowModes.View, ignoreModifications: true },
            });
        },
        updateFilters: (filters) => {
            // if (apiRef) {  // this feature cancelled.Transport managers want to keep the changes.
            //     const changedIds = Object.keys(get().initialValues);
            //     if (changedIds) {
            //         changedIds.forEach((id) => get().actions.handleRowModesModelCancel(id, apiRef));
            //     }
            // }
            set({ filters });
        },

        getChangedKeys: (newRow: IPlanPassenger<"api">, oldRow: IPlanPassenger<"api">) => {
            const changes: PlanEditableFields = {};

            if (newRow.location !== oldRow.location) {
                changes.location = newRow.location;
            }

            // Airport
            if (newRow.airport !== oldRow.airport) {
                changes.airport = newRow.airport || "";
            }

            // On paste string, convert to DateTime
            if (typeof newRow.pickUp === "string") newRow.pickUp = DateTime.fromISO(newRow.pickUp);
            if (typeof newRow.dropOff === "string") newRow.dropOff = DateTime.fromISO(newRow.dropOff);

            // DateTime
            if (newRow.pickUp !== undefined && newRow.pickUp?.isValid && newRow.pickUp !== oldRow.pickUp)
                changes.pickUp = newRow.pickUp;

            if (newRow.transferType && newRow.transferType != oldRow.transferType) {
                changes.transferType = newRow.transferType;
            }

            if (newRow.status && newRow.status != oldRow.status) {
                changes.status = newRow.status;
            }

            if (
                newRow.location &&
                newRow.airport &&
                newRow.driverId !== undefined &&
                newRow.driverId !== oldRow.driverId
            ) {
                changes.driverId = newRow.driverId;
            }

            return [changes, Object.keys(changes).length] as [PlanEditableFields, number];
        },
        processRowUpdate: (newRow: IPlanPassenger<"api">, oldRow: IPlanPassenger<"api">) => {
            const [changes, changesAmount] = get().actions.getChangedKeys(newRow, oldRow);

            if (!changesAmount) return oldRow;

            const newChanges = { ...get().changes };

            try {
                for (const key of Object.keys(changes)) {
                    const initialData = get().initialValues[newRow.id];
                    const initialValue = initialData && get().initialValues[oldRow.id][key as keyof PlanEditableFields];
                    const changedVal = changes[key as keyof PlanEditableFields];

                    if (changedVal === initialValue) {
                        delete changes[key as keyof PlanEditableFields];

                        if (newChanges[newRow.id] && !Object.keys(newChanges[newRow.id]).length)
                            delete newChanges[newRow.id];
                    }

                    if (!initialData || !(key in initialData)) {
                        const newVal = { ...get().initialValues };
                        newVal[newRow.id] = {
                            ...(newVal[newRow.id] || {}),
                            [key]: oldRow[key as keyof IPlanPassenger<"api">] as string & DateTime<boolean>,
                        };

                        set({ initialValues: newVal });
                    }
                }
            } catch (err) {
                console.error(err, "err");
            }

            if (newChanges[newRow.id] || Object.keys(changes).length) {
                newChanges[newRow.id] = {
                    ...(newChanges[newRow.id] || {}),
                    ...changes,
                };
            }

            set({ changes: newChanges });

            return newRow;
        },
    },
}));

export const usePlanRowModesModel = () => usePlanTableStore((state) => state.rowModesModel);
export const usePlanIsFullScreen = () => usePlanTableStore((state) => state.isFullScreen);
export const usePlanShowCreatePassengerDialog = () => usePlanTableStore((state) => state.showCreatePassengerDialog);
export const usePlanChanges = () => usePlanTableStore((state) => state.changes);
export const usePlanShowConfirmationList = () => usePlanTableStore((state) => state.showConfirmationList);
export const usePlanRowChanges = (id: GridRowId) => usePlanTableStore((state) => state.changes[id]);
export const usePlanIsChanged = (id: GridRowId, field: keyof PlanEditableFields) =>
    usePlanTableStore((state) => state.changes[id] && field in state.changes[id]);
export const usePlanChangedVal = (id: GridRowId, field: keyof PlanEditableFields) =>
    usePlanTableStore((state) => state.changes[id] && state.changes[id][field]);
export const usePlanInitialVal = (id: GridRowId, field: keyof PlanEditableFields) =>
    usePlanTableStore((state) => state.initialValues[id] && state.initialValues[id][field]);
export const usePlanShowDeletePassengerDialog = () => usePlanTableStore((state) => state.showDeletePassengerDialog);
export const usePlanShowUpdateCompanionsDialog = () => usePlanTableStore((state) => state.showUpdateCompanionsDialog);
export const usePlanShowPassengerToWelcomeTeamDialog = () =>
    usePlanTableStore((state) => state.showPassengerToWelcomeTeamDialog);
export const usePlanShowPassengerHistoryDialog = () => usePlanTableStore((state) => state.showPassengerHistoryDialog);

export const usePlanFilters = () => usePlanTableStore((state) => state.filters);
export const usePlanActions = () => usePlanTableStore((state) => state.actions);
export const usePlanSearchItemId = () => usePlanTableStore((state) => state.searchItemId);
